/**
 * Utility functions for case-insensitive object key operations
 */

/**
 * Find a key in an object case-insensitively
 * @param obj - The object to search in
 * @param targetKey - The key to find (case-insensitive)
 * @returns The actual key name if found, undefined otherwise
 */
export const findKeyIgnoreCase = (obj: Record<string, unknown>, targetKey: string): string | undefined => {
  if (!obj || typeof obj !== 'object') return undefined;
  const keys = Object.keys(obj);
  return keys.find(key => key.toLowerCase() === targetKey.toLowerCase());
};

/**
 * Get a value from an object using case-insensitive key lookup
 * @param obj - The object to get the value from
 * @param targetKey - The key to look for (case-insensitive)
 * @returns The value if found, undefined otherwise
 */
export const getValueIgnoreCase = (obj: Record<string, unknown>, targetKey: string): unknown => {
  const actualKey = findKeyIgnoreCase(obj, targetKey);
  return actualKey ? obj[actualKey] : undefined;
};

/**
 * Convert a value to a number for threshold comparisons
 * @param value - The value to convert (can be number, string, or other)
 * @returns A valid number or undefined if conversion fails
 */
export const toNumericThreshold = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

/**
 * Get threshold value with fallback logic for project-specific and general thresholds
 * @param backgroundData - The schema background data object
 * @param projectType - The project type to look for specific threshold
 * @param thresholdKey - The key name for general threshold (default: 'treshold')
 * @returns Numeric threshold value or undefined
 */
export const getThresholdValue = (
  backgroundData: Record<string, unknown> | undefined,
  projectType: string | undefined,
  thresholdKey: string = 'treshold'
): number | undefined => {
  if (!backgroundData) return undefined;

  // First try to find project-specific threshold
  if (projectType) {
    const projectTypeData = getValueIgnoreCase(backgroundData, 'projectType');
    if (projectTypeData && typeof projectTypeData === 'object' && projectTypeData !== null) {
      const projectTypeThreshold = getValueIgnoreCase(projectTypeData as Record<string, unknown>, projectType);
      const numericProjectThreshold = toNumericThreshold(projectTypeThreshold);
      if (numericProjectThreshold !== undefined) {
        return numericProjectThreshold;
      }
    }
  }

  // Fallback to general threshold
  const generalThreshold = getValueIgnoreCase(backgroundData, thresholdKey);
  return toNumericThreshold(generalThreshold);
};